import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';

import { ColumnFilterConfig, ColumnFilterCriterion, isCriterionActive } from '../table-filter.models';
import { ColumnFilterEditorComponent } from '../column-filter-editor/column-filter-editor.component';

/** Opens the editor for one column and emits updated filter criteria. */
@Component({
  selector: 'app-column-filter',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, ColumnFilterEditorComponent],
  templateUrl: './column-filter.component.html',
  styleUrls: ['./column-filter.component.scss'],
})
export class ColumnFilterComponent {
  @Input({ required: true }) config!: ColumnFilterConfig;
  @Input() criterion?: ColumnFilterCriterion;

  @Output() criterionChange = new EventEmitter<ColumnFilterCriterion | undefined>();

  private elementRef = inject(ElementRef);

  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;
  @ViewChild('editor') editor?: ColumnFilterEditorComponent;

  get isActive(): boolean {
    return isCriterionActive(this.criterion);
  }

  get badgeCount(): number {
    if (!this.criterion) return 0;
    if (this.criterion.type === 'categorical') {
      return this.criterion.values.length;
    }
    return this.isActive ? 1 : 0;
  }

  get filterButtonAriaLabel(): string {
    const base = `Filter by ${this.config?.columnLabel || ''}`;
    return this.isActive ? `${base} (${this.badgeCount} active)` : base;
  }

  onButtonClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  onButtonKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.stopPropagation();
    }
  }

  onMenuOpened(): void {
    setTimeout(() => {
      this.editor?.focusFirstInput();
    });
  }

  onMenuContentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeMenu();
      event.stopPropagation();
      return;
    }
    if (event.key === 'Tab') {
      return;
    }
    event.stopPropagation();
  }

  onCriterionChange(newCriterion: ColumnFilterCriterion | undefined): void {
    this.criterion = newCriterion;
    this.criterionChange.emit(newCriterion);
  }

  clearFilter(): void {
    this.editor?.clearFilter();
    this.criterion = undefined;
    this.criterionChange.emit(undefined);
    this.closeMenu();
  }

  closeMenu(): void {
    this.menuTrigger?.closeMenu();
    this.focusTrigger();
  }

  focusTrigger(): void {
    const btn = this.elementRef.nativeElement.querySelector('button.filter-button') as HTMLElement | null;
    btn?.focus();
  }
}
